import { LocalDictionaryFactory } from '../../api/api.module'
import { LoggerService } from '../../common/common.module'

import { IUserSession, EventsNames, LocalizationHelper, TemplateHelper, ConfigHelper } from '../../../helpers'

import { Activity, Enums } from '../../../models/common'

import { Details, DetailsScope, DetailsAccordion, DetailsAccordionSection } from '../../common/common.details'
import { DetailsPopupOptions, DetailsOptions } from '../../common/details-popup/details-popup.directive'
import { OpConsumerType } from '../../../models/core/op-consumer-type'
import { ConsumerTypeHelper } from '../consumer-types.helper'



interface ConsumerTypeDetailsScope extends DetailsScope {
    CONSUMER_TYPE_PARAMS_ACCORDION_NAME: string
    CONSUMER_TYPE_COMMON_TAB_NAME: string
    NO_ACTIVE_ACCORDIONS: string
    EDIT: string

    id: number

    details: any

    deleteConfirmationOptions: {
        title: string

        onConfirmed: () => void
    }
}

export class ConsumerTypeDetailDirectiveController extends Details<ConsumerTypeDetailsScope> {

    // #region Members

    private idConsumerType: number
    private consumerType: OpConsumerType

    // #endregion


    // #region Ctor

    constructor(
        $scope: ConsumerTypeDetailsScope,
        $q: ng.IQService,
        $timeout: ng.ITimeoutService,
        logger: LoggerService,
        localDictionary: LocalDictionaryFactory,
        userSession: IUserSession,
        referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {
        super($scope, $timeout, $q, logger, localDictionary, null, userSession, referenceType)

        this.setEvents()
        this.setOptions()
        this.setTemplateConsts()
        this.setConfirmationsOptions()

        if (this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_TYPE_LIST)) {
            this.configureTabs(this.getTabs())
        }
    }

    // #endregion


    // #region Events

    private onOpen(options: DetailsOptions): void {
        this.$scope.detailsOpenParams = options

        this.idConsumerType = options.referenceObjectId

        this.onClear()
        this.showPopup()
        this.onRefresh(null)
    }

    private onRefresh(toRefresh: OpConsumerType = null): void {
        this.$scope.options.isDataLoaded = false

        if (!toRefresh) {
            ConsumerTypeHelper
                .getConsumerType(this.userSession, this.idConsumerType)
                .then((consumerType: OpConsumerType) => {
                    this.setBasicParameters(consumerType)
                    this.consumerType = consumerType
                    this.$scope.showTabs = true
                })
                .fail(() => {
                    this.hidePopup()
                })
                .always(() => {
                    this.$scope.options.isDataLoaded = true
                })
        }
        else {
            this.consumerType = toRefresh

            this.setBasicParameters(toRefresh);
            this.$scope.showTabs = true
            this.$scope.options.isDataLoaded = true
        }
    }

    private onClear(): void {
        this.$scope.popupTitle = ''
        this.$scope.showTabs = false
    }

    private onClose(): void {
        this.hidePopup()
    }

    private onConfirmDelete(): void {
        ConsumerTypeHelper
            .deleteConsumerType(this.userSession, this.consumerType)
            .then(() => {
                this.$scope.$root.$broadcast(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName))
                this.onClose()
            })
    }

    private onDelete(): void {
        this.$scope.$broadcast(
            EventsNames.OPEN_CONFIRMATION_POPUP + this.$scope.detailsPopupId,
            LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName)
        )
    }

    private onParamsEdit(): void {
        this.$scope.$root.$broadcast(
            EventsNames.INITIALIZE_CREATE_EDIT_POPUP,
            ConsumerTypeHelper.getCreateEditOptions(this.userSession, this.idConsumerType, this.consumerType)
        )
    }

    // #endregion


    // #region Parameters

    private setBasicParameters(consumerType: OpConsumerType): void {
        this.$scope.details = ConsumerTypeHelper.getParmetersFields(consumerType)
    }

    // #endregion


    // #region Setup 

    public getAccordions(): DetailsAccordion[] {
        const accordions: DetailsAccordion[] = []
        const items: DetailsAccordionSection[] = []

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_TYPE_DETAILS_HIDDEN_UI_PROPERTIES, Enums.UIObjectProperties.ConsumerTypeParameters)) {
            items.push({
                name: this.$scope.CONSUMER_TYPE_PARAMS_ACCORDION_NAME,
                showInTab: this.$scope.CONSUMER_TYPE_COMMON_TAB_NAME,
                showStatus: false,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.BasicParameters),
                visible: true,

                canEdit: this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_TYPE_EDIT)
                    && !this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_TYPE_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.ConsumerTypeParameters),

                editMethod: () => this.onParamsEdit(),
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, null, 0, items))
        }

        return accordions
    }

    public getTabs(): any[] {
        return [{
            template: TemplateHelper.getIconWithText(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Common), ConfigHelper.Icon.HOME, this.userSession),

            name: this.$scope.CONSUMER_TYPE_COMMON_TAB_NAME
        }]
    }

    private setConfirmationsOptions(): void {
        this.$scope.deleteConfirmationOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),

            onConfirmed: () => this.onConfirmDelete()
        }
    }

    private setEvents(): void {
        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            (event: ng.IAngularEvent, ...args: any[]) => this.onOpen(args[3])
        )

        this.$scope.$on(
            EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName) + this.$scope.id,
            () => this.onClose()
        )

        this.$scope.$on(EventsNames.CLOSE_CREATE_EDIT_POPUP, (event: ng.IAngularEvent, ...args: any[]) => this.onRefresh(args[0]))
    }

    private setOptions(): void {
        this.accordionsNameToShow = this.$scope.CONSUMER_TYPE_COMMON_TAB_NAME

        this.$scope.options = <DetailsPopupOptions>{
            id: this.$scope.id,

            closeEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__CLOSE_DETAILS_POPUP, this.moduleName),
            delHasPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.CONSUMER_TYPE_DEL) ? Activity[Activity.CONSUMER_TYPE_DEL] : null,
            openEventName: EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__OPEN_DETAILS_POPUP, this.moduleName),
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DETAILS, this.moduleName),

            deleteMethod: () => this.onDelete(),
            getAccordionDataMethod: () => this.getAccordionData(),
            getAccordionSelectionMethod: () => this.getAccordionSelection(),
            updateAccordionMethod: (names: any[]) => this.updateAccordions(names)
        }
    }

    private setTemplateConsts(): void {
        this.$scope.CONSUMER_TYPE_COMMON_TAB_NAME = 'CONSUMER_TYPE_COMMON_TAB_NAME'
        this.$scope.CONSUMER_TYPE_PARAMS_ACCORDION_NAME = 'CONSUMER_TYPE_PARAMS_ACCORDION_NAME'
        this.$scope.NO_ACTIVE_ACCORDIONS = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NoActiveAccordions)
        this.$scope.EDIT = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Edit)
    }

    // #endregion

    resetData() { this.onClear() }//DO USUNIECIA!!! - zastąpić onClear
    loadData(id: number, objects: any[]) { this.onOpen(null) }//DO USUNIECIA!!! - zastąpić onOpen
    updateData(result: any) { this.onRefresh(result) }//DO USUNIECIA!!! - zastąpić onRefresh
}